import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Save } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatCurrencyWithSymbol } from '../../../utils/currencyUtils';

const BudgetSectionEditor = ({
  title,
  description,
  lines,
  fields,
  emptyLine,
  readOnly,
  saving,
  onSave,
  additionalContent,
  deriveLine,
  getSectionTotal,
  sectionTotalLabel,
  onFieldChange,
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(lines || []);
  const [error, setError] = useState('');

  useEffect(() => {
    setDraft((lines || []).map((line) => (
      deriveLine ? deriveLine({ ...line }) : { ...line }
    )));
  }, [deriveLine, lines]);

  const sectionTotal = useMemo(
    () => (getSectionTotal ? getSectionTotal(draft) : null),
    [draft, getSectionTotal]
  );

  const updateLine = (index, key, value) => {
    setDraft((current) => current.map((line, lineIndex) => (
      lineIndex === index
        ? (() => {
          const updated = { ...line, [key]: value };
          const normalized = onFieldChange
            ? onFieldChange(updated, key, value, line)
            : updated;
          return deriveLine ? deriveLine(normalized) : normalized;
        })()
        : line
    )));
  };

  const addLine = () => {
    const line = deriveLine ? deriveLine({ ...emptyLine }) : { ...emptyLine };
    setDraft((current) => [...current, line]);
  };

  const removeLine = (index) => {
    setDraft((current) => current.filter((_, lineIndex) => lineIndex !== index));
  };

  const save = async () => {
    const missing = draft.some((line) => fields.some(
      (field) => field.required
        && (!field.visibleWhen || field.visibleWhen(line))
        && !String(line[field.key] ?? '').trim()
    ));
    if (missing) {
      setError(t('budget.errors.requiredFields'));
      return;
    }
    setError('');
    await onSave(draft);
  };

  const renderField = (field, line, index) => {
    const rawValue = line[field.key] ?? field.defaultValue ?? '';
    const value = field.format === 'currency' && rawValue !== ''
      ? formatCurrencyWithSymbol(rawValue)
      : field.format === 'percent' && rawValue !== '' && rawValue !== null
        ? Number(rawValue).toLocaleString(undefined, {
          style: 'percent',
          maximumFractionDigits: 2,
        })
        : rawValue;
    if (field.type === 'switch') {
      return (
        <FormControlLabel
          control={(
            <Switch
              checked={value !== false}
              onChange={(event) => updateLine(index, field.key, event.target.checked)}
              disabled={readOnly}
              inputProps={{ 'aria-label': t(field.labelKey) }}
            />
          )}
          label={t(field.labelKey)}
        />
      );
    }

    return (
      <TextField
        select={field.type === 'select'}
        type={field.type === 'number' ? 'number' : 'text'}
        label={t(field.labelKey)}
        value={value}
        onChange={(event) => updateLine(index, field.key, event.target.value)}
        disabled={readOnly || field.readOnly}
        required={field.required}
        fullWidth
        multiline={field.type === 'textarea'}
        minRows={field.type === 'textarea' ? 2 : undefined}
        helperText={field.helperTextKey ? t(field.helperTextKey) : undefined}
        inputProps={{
          min: field.min,
          max: field.max,
          step: field.step,
          'aria-label': t(field.labelKey),
        }}
      >
        {(field.options || []).map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {t(option.labelKey)}
          </MenuItem>
        ))}
      </TextField>
    );
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography variant="h6" component="h2">{title}</Typography>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {description}
          </Typography>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {additionalContent}
      {sectionTotal !== null && sectionTotal !== undefined && (
        <Paper
          variant="outlined"
          sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}
          aria-live="polite"
        >
          <Typography variant="body2" color="text.secondary">
            {sectionTotalLabel}
          </Typography>
          <Typography variant="h5" component="p">
            {formatCurrencyWithSymbol(sectionTotal)}
          </Typography>
        </Paper>
      )}

      <Stack spacing={2}>
        {draft.map((line, index) => (
          <Paper key={line.id || `new-${index}`} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">
                {t('budget.lineNumber', { number: index + 1 })}
              </Typography>
              {!readOnly && (
                <IconButton
                  onClick={() => removeLine(index)}
                  aria-label={t('budget.actions.removeLine', { number: index + 1 })}
                  color="error"
                  sx={{ minWidth: 44, minHeight: 44 }}
                >
                  <Delete />
                </IconButton>
              )}
            </Stack>
            <Grid container spacing={2}>
              {fields.filter((field) => (
                !field.visibleWhen || field.visibleWhen(line)
              )).map((field) => (
                <Grid key={field.key} size={{ xs: 12, sm: field.sm || 4 }}>
                  {renderField(field, line, index)}
                </Grid>
              ))}
            </Grid>
          </Paper>
        ))}
      </Stack>

      {!readOnly && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="space-between"
          sx={{ mt: 3 }}
        >
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={addLine}
            aria-label={t('budget.actions.addLine')}
            sx={{ minHeight: 44 }}
          >
            {t('budget.actions.addLine')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={save}
            disabled={saving}
            aria-label={t('budget.actions.saveSection')}
            sx={{ minHeight: 44 }}
          >
            {saving ? t('budget.actions.saving') : t('budget.actions.saveSection')}
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export default BudgetSectionEditor;
